import * as React from "react";
import { format, parse } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// Value format: "YYYY-MM-DDTHH:mm" (same as <input type="datetime-local">)
export interface DateTimePickerProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  /** "datetime" outputs YYYY-MM-DDTHH:mm, "date" outputs YYYY-MM-DD */
  mode?: "datetime" | "date";
}

export function DateTimePicker({ value, onChange, placeholder, className, mode = "datetime" }: DateTimePickerProps) {
  const isDate = mode === "date";
  const parsed = React.useMemo(() => {
    if (!value) return null;
    const d = new Date(isDate ? `${value}T00:00` : value);
    return isNaN(d.getTime()) ? null : d;
  }, [value, isDate]);

  const time = parsed ? format(parsed, "HH:mm") : "09:00";

  const update = (date: Date | undefined, t: string) => {
    if (!date) return;
    const d = new Date(date);
    if (isDate) {
      onChange(format(d, "yyyy-MM-dd"));
    } else {
      const [hh, mm] = (t || "00:00").split(":").map(Number);
      d.setHours(hh || 0, mm || 0, 0, 0);
      onChange(format(d, "yyyy-MM-dd'T'HH:mm"));
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn("w-full justify-start text-left font-normal", !parsed && "text-muted-foreground", className)}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {parsed ? format(parsed, isDate ? "PPP" : "PPP p") : <span>{placeholder ?? (isDate ? "Pick a date" : "Pick date & time")}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={parsed ?? undefined}
          onSelect={(d) => update(d, time)}
          initialFocus
          className={cn("p-3 pointer-events-auto")}
        />
        {!isDate && (
          <div className="border-t p-3 flex items-center gap-2">
            <Label className="text-xs text-muted-foreground">Time</Label>
            <Input
              type="time"
              value={time}
              onChange={(e) => update(parsed ?? new Date(), e.target.value)}
              className="w-32"
            />
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}